import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { Mention } from '../data/types';
import { chapterOf, hostParagraph } from '../features/reader/segments';
import { readSelection, writeSelection, type UrlSelection } from './url';
import { useAppData } from './AppData';

/**
 * Única fuente de verdad de la selección (spec §9.2): la URL.
 * `origin` no va en la URL: solo decide quién reacciona (evita bucles texto↔mapa).
 * `jump` es una orden de desplazamiento del lector que se consume una vez.
 */
export type Origin = 'text' | 'map' | 'list' | 'card' | 'url';

interface SelectionApi extends UrlSelection {
  origin: Origin;
  jump: { paragraphId: string; mentionId: string | null; nonce: number } | null;
  consumeJump: () => void;
  selectPlace: (placeId: string | null, origin: Origin) => void;
  selectMention: (m: Mention, origin: Origin) => void;
  readInContext: (m: Mention) => void;
  setReadingParagraph: (paragraphId: string) => void;
  announce: (msg: string) => void;
  announcement: string;
}

const Ctx = createContext<SelectionApi | null>(null);

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const data = useAppData();
  const sel = readSelection(params);
  const originRef = useRef<Origin>('url');
  const [jump, setJump] = useState<SelectionApi['jump']>(() =>
    sel.paragraph ? { paragraphId: sel.paragraph, mentionId: sel.mention, nonce: 0 } : null,
  );
  const [announcement, setAnnouncement] = useState('');
  const announce = useCallback((msg: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(msg));
  }, []);

  const selectPlace = useCallback((placeId: string | null, origin: Origin) => {
    originRef.current = origin;
    setParams((p) => writeSelection(p, { place: placeId, mention: null }), { replace: false });
    const place = placeId ? data.placeById.get(placeId) : null;
    if (place) {
      const n = data.mentionsByPlace.get(placeId!)?.length ?? 0;
      announce(`Seleccionado: ${place.properties.canonicalName}. ${n} ${n === 1 ? 'mención' : 'menciones'} en el texto.`);
    }
  }, [setParams, data, announce]);

  const selectMention = useCallback((m: Mention, origin: Origin) => {
    originRef.current = origin;
    setParams((p) => writeSelection(p, { place: m.placeId, mention: m.id }), { replace: false });
    const place = data.placeById.get(m.placeId);
    if (place && origin === 'text') announce(`${place.properties.canonicalName} destacado en el mapa.`);
  }, [setParams, data, announce]);

  const readInContext = useCallback((m: Mention) => {
    originRef.current = 'card';
    const chapter = chapterOf(m.paragraphId);
    const para = hostParagraph(m.paragraphId);
    const next = writeSelection(new URLSearchParams(location.pathname.startsWith('/leer') ? params : undefined), { place: m.placeId, mention: m.id, paragraph: para });
    const ciudad = params.get('ciudad');           // la ciudad activa nunca se pierde al saltar de sección
    if (ciudad) next.set('ciudad', ciudad);
    setJump({ paragraphId: m.paragraphId, mentionId: m.id, nonce: Date.now() });
    navigate(`/leer/${chapter}?${next.toString()}`);
    announce(`Pasaje abierto en el capítulo ${chapter.slice(-1)}, página ${m.printedPage ?? 'sin número'}.`);
  }, [navigate, params, location.pathname, announce]);

  const setReadingParagraph = useCallback((paragraphId: string) => {
    setParams((p) => (p.get('paragraph') === paragraphId ? p : writeSelection(p, { paragraph: paragraphId })), { replace: true });
  }, [setParams]);

  const api: SelectionApi = {
    ...sel,
    origin: originRef.current,
    jump,
    consumeJump: () => setJump(null),
    selectPlace, selectMention, readInContext, setReadingParagraph, announce, announcement,
  };
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useSelection(): SelectionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSelection fuera de SelectionProvider');
  return v;
}
